import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { ModelOrderResponse, ModelPaymentPlanResponse } from '@layup/api-sdk';
import { NetworkError } from '../errors';
import { getOrderByIdWithPlansPaymentsAndConnectors } from '../services/orders';
import { addMetric, createException, createWarning } from '../utils/monitoring.utils';

export const useOrderStore = defineStore('orders', {
  state: () => {
    const orders = ref<ModelOrderResponse[]>();
    const order = ref<ModelOrderResponse | undefined>(undefined);
    const loading = ref<boolean>(false);
    const error = ref<string | undefined>(undefined);
    return { orders, order, loading, error };
  },
  actions: {
    getPaymentPlan(): ModelPaymentPlanResponse | undefined {
      if (!this.order) {
        return undefined;
      }
      const plan = this.order?.plans?.[0];
      return plan;
    },

    async getOrderById(orderId: string) {
      try {
        this.order = undefined;
        this.error = undefined;
        this.loading = true;
        this.order = await getOrderByIdWithPlansPaymentsAndConnectors(orderId);
        addMetric('order', { status: 'success', channel: 'merchant' });
      } catch (error) {
        if (error instanceof NetworkError) {
          createWarning(error);
          addMetric('order', { status: 'network_error', channel: 'merchant' });
        } else {
          createException(error);
          addMetric('order', { status: 'error', channel: 'merchant' });
        }
        this.error = (error as Error).message;
      } finally {
        this.loading = false;
      }
    },

    setOrders(orders: ModelOrderResponse[]) {
      this.orders = orders;
    },

    updateOrder(order: ModelOrderResponse) {
      this.order = order;
      const index = this.orders?.findIndex((o) => o._id === order._id);
      if (index !== undefined && index !== -1 && this.orders) {
        this.orders[index] = order;
      }
    },
    reset() {
      this.$reset();
    },
  },
  persist: true,
});
